import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Grid, LinearProgress } from '@material-ui/core';
import { APIProvider, Map, Marker, InfoWindow } from '@vis.gl/react-google-maps';
import SearchSidebar from './SearchSidebar';
import { makeStyles } from '@material-ui/core/styles';
import { map, set } from 'lodash';
import gql from 'graphql-tag';

const USER_SEARCH_QUERY = gql`
  query UserSearch(
    $prompt: String, 
    $page: Int, 
    $perPage: Int, 
    $locationWithin: String,
    $transitionGoalsName: [String!],
    $planningPreference: String,
    $province: String,
    $isStudent: Boolean,
    $ampTraineeStatus: Boolean,
    $hasPartialSales: Boolean,
    $partialSaleLocations: [String!],
    $annualRevenueGt: Float,
    $annualRevenueLt: Float,
    $grossProductionGt: Float,
    $grossProductionLt: Float,
    $assetsUnderManagementGt: Float,
    $assetsUnderManagementLt: Float,
    $yearsInPracticeGt: Int,
    $yearsInPracticeLt: Int,
    $numberOfClientsGt: Int,
    $numberOfClientsLt: Int,
    $designations: [ID!], 
    $affiliations: [ID!], 
    $languages: [String!], 
    $advisorLicenses: [String!], 
    $ethnicity: String,
    $faith: String,
    $interests: [String!], 
    $sports: [String!], 
    $primaryMarkets: [String!], 
    $specializedBusinesses: [String!], 
    $tags: [String!], 
    $discretionaryInvestmentModels: Boolean,
    $name: String
  ) {
    getCurrentUser {
      id
      user_name
      features
      __typename
    }
    getCurrentUser {
      id
      motivation
      searchable_transition_goals
    }
    getGrowthTransitionGoals
    availableStrengths
    availableDesignations {
      id
      name
      __typename
    }
    company {
      id
      name
      country
      available_primary_markets
      features
      business_type
      languages {
        id
        name
        __typename
      }
      interests {
        id
        name
        __typename
      }
      __typename
    }
    availableAffiliations {
      id
      name
      __typename
    }
    userSearch(
      prompt: $prompt
      page: $page
      perPage: $perPage
      locationWithin: $locationWithin
      transitionGoalsName: $transitionGoalsName
      planningPreference: $planningPreference
      province: $province
      isStudent: $isStudent
      ampTraineeStatus: $ampTraineeStatus
      hasPartialSales: $hasPartialSales
      partialSaleLocations: $partialSaleLocations
      annualRevenueGt: $annualRevenueGt
      annualRevenueLt: $annualRevenueLt
      grossProductionGt: $grossProductionGt
      grossProductionLt: $grossProductionLt
      assetsUnderManagementGt: $assetsUnderManagementGt
      assetsUnderManagementLt: $assetsUnderManagementLt
      yearsInPracticeGt: $yearsInPracticeGt
      yearsInPracticeLt: $yearsInPracticeLt
      numberOfClientsGt: $numberOfClientsGt
      numberOfClientsLt: $numberOfClientsLt
      designations: $designations
      affiliations: $affiliations
      languages: $languages
      advisorLicenses: $advisorLicenses
      ethnicity: $ethnicity
      faith: $faith
      interests: $interests
      sports: $sports
      primaryMarkets: $primaryMarkets
      specializedBusinesses: $specializedBusinesses
      tags: $tags
      discretionaryInvestmentModels: $discretionaryInvestmentModels
      name: $name
    ) {
      total_count
      items {
        id
        user_name
        allowed_name
        fb_user_name
        annual_revenue
        assets_under_management
        bio
        city
        county
        map_latitude
        map_longitude
        allowed_name
        __typename
      }
      aiResponse
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    margin: theme.spacing(2),
  },
  ai_copy: {
    fontSize: '14px',
    color: '#555',
    margin: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: '#f9f9f9',
    borderRadius: '4px',
    border: '1px solid #ddd',
  },
}));

const UserMapSearch = () => {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([
    {type: 'ai',
    message: "Hi! I'm your search assistant tell me exactly what you're looking for!"}]);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const classes = useStyles();

  const defaultSearchParams = {
    locationWithin: '',
    province: '',
    languages: [],
    hasPartialSales: false,
    annualRevenueGt: '',
    annualRevenueLt: '',
    assetsUnderManagementGt: '',
    assetsUnderManagementLt: '',
    designations: [],
    affiliations: [],
    primaryMarkets: [],
    planningPreference: '',
  };

  const [searchParams, setSearchParams] = useState(defaultSearchParams);

  const sanitizeNumericValues = (params) => {
  const numericFields = [
    'annualRevenueGt',
    'annualRevenueLt',
    'assetsUnderManagementGt',
    'assetsUnderManagementLt',
    'grossProductionGt',
    'grossProductionLt',
    'yearsInPracticeGt',
    'yearsInPracticeLt',
    'numberOfClientsGt',
    'numberOfClientsLt',
    'recurringRevenueGt',
    'recurringRevenueLt',
    'commissionRevenueGt',
    'commissionRevenueLt',
    'feeBasedRevenueGt',
    'feeBasedRevenueLt',
    'trailerRevenueGt',
    'trailerRevenueLt',
    'directRevenueGt',
    'directRevenueLt'
  ];

    const sanitizedParams = { ...params };
    
    numericFields.forEach(field => {
      const value = params[field];
      if (value === '' || value === null || value === undefined) {
        delete sanitizedParams[field];
      } else {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          sanitizedParams[field] = numValue;
        } else {
          delete sanitizedParams[field];
        }
      }
    });

    return sanitizedParams;
  };

  const { loading, error, data, refetch } = useQuery(USER_SEARCH_QUERY, {
    variables: {
      page: 1,
      perPage: 1000, 
      ...sanitizeNumericValues(searchParams)
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });

  const handleMarkerClick = (user) => {
    // setSelectedMarker(user);
    if(document.x) {
      document.x.a = user.fb_user_name;
    }
  };

  const handleMapBoundsChanged = (bounds) => {
    setMapBounds(bounds);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent default for multiline TextField
      handleSearchSubmit();
    }
  };

  const handleSearchChange = (field, value) => {
    // Convert string 'true'/'false' to actual boolean for boolean fields
    const booleanFields = ['isStudent', 'ampTraineeStatus', 'hasPartialSales', 'discretionaryInvestmentModels'];
    const processedValue = booleanFields.includes(field) && value !== null ? Boolean(value) : value;
  
    setSearchParams(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  // Memoize valid users to prevent unnecessary re-renders
  const validUsers = useMemo(() => {
    const users = data?.userSearch?.items || [];
    return users.filter(user => (
      user.map_latitude && 
      user.map_longitude && 
      !isNaN(parseFloat(user.map_latitude)) && 
      !isNaN(parseFloat(user.map_longitude))
    ));
  }, [data]);

  // Calculate center and bounds based on markers
  const mapConfig = useMemo(() => {
    if (!validUsers?.length) {
      return {
        center: { lat: 43.6532, lng: -79.3832 }, // Default to Toronto
        zoom: 4
      };
    }

    // Calculate the center by averaging all coordinates
    const lats = validUsers.map(user => parseFloat(user.map_latitude));
    const lngs = validUsers.map(user => parseFloat(user.map_longitude));
    
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    return {
      center: {
        lat: (minLat + maxLat) / 2,
        lng: (minLng + maxLng) / 2
      },
      zoom: 4,
      bounds: {
        north: maxLat,
        south: minLat,
        east: maxLng,
        west: minLng
      }
    };
  }, [validUsers]);

  const formatConversationHistory = (history) => {
    return history
      .map(item => (item.type === 'user' ? item.message : null))
      .filter(Boolean)
      .join('\n');
  };

  const handleSearchSubmit = () => {
    if (!searchInputValue.trim()) return;
    
    setIsWaitingForResponse(true);
    // Add user message to conversation
    const newUserMessage = {
      type: 'user',
      message: searchInputValue,
      timestamp: new Date().toISOString()
    };
    
    setConversationHistory(prev => [...prev, newUserMessage]);

    // Get recent conversation history (last 20 messages)
    const recentHistory = [...conversationHistory.slice(-19), newUserMessage];
    const historyString = formatConversationHistory(recentHistory);
    
    // Set the prompt in searchParams with history context
    setSearchParams(prev => ({
      ...prev,
      prompt: `Previous messaages: ${historyString}. Current request: ${searchInputValue}`
    }));
    
    setSearchInputValue('');
  };

  // Update conversation when we get AI response
  useEffect(() => {
    if (data?.userSearch?.aiResponse && searchParams.prompt) {
      setIsWaitingForResponse(false);
      setConversationHistory(prev => {
        // Avoid duplicate responses
        if (prev.length > 0 && prev[prev.length - 1].message === data.userSearch.aiResponse) {
          return prev;
        }
        const newHistory = [...prev, {
          type: 'ai',
          message: data.userSearch.aiResponse,
          timestamp: new Date().toISOString()
        }];
        
        // Add count message
        if (data.userSearch.total_count > 0) {
          newHistory.push({
            type: 'ai',
            message: `${data.userSearch.total_count} advisors found.`,
            timestamp: new Date().toISOString()
          });
        } else {
          newHistory.push({
            type: 'ai',
            message: 'I couldn\'t find any advisors matching those criteria. Try broadening your search.',
            timestamp: new Date().toISOString()
          });
        }
        
        return newHistory;
      });
    }
  }, [data?.userSearch?.aiResponse]);

  if (error) return <div>Error: {error.message}</div>;
  const allowAISearch = data?.company?.features?.includes('ai_search') || false;

  return (
    <Grid container spacing={2} direction="column">
      <Grid item xs={12}>
        <div style={{ width: '100%', height: '500px' }}> {/* Fixed height container */}
          <APIProvider apiKey="AIzaSyBVLq93fZSKcDyU0_MuvWnRyHE-3poEGs8">
            <Map
              defaultCenter={mapConfig.center}
              defaultZoom={mapConfig.zoom}
              defaultBounds={mapConfig.bounds}
              minZoom={3}
              maxZoom={10}
              gestureHandling={'greedy'}
              disableDefaultUI={true}
              zoomControl={true}
              style={{ width: '100%', height: '100%' }}
            >
              {validUsers.map((user) => (
                <Marker
                  key={user.id}
                  position={{
                    lat: parseFloat(user.map_latitude),
                    lng: parseFloat(user.map_longitude)
                  }}
                  onClick={() => handleMarkerClick(user)}
                  options={{
                    title: user.allowed_name
                  }}
                />
              ))}
              
              {selectedMarker && (
                <InfoWindow
                  position={{
                    lat: parseFloat(selectedMarker.map_latitude),
                    lng: parseFloat(selectedMarker.map_longitude)
                  }}
                  onCloseClick={() => setSelectedMarker(null)}
                >
                  <div style={{ padding: '8px' }}>
                    <h3 style={{ margin: '0 0 8px 0' }}>{selectedMarker.allowed_name}</h3>
                    {selectedMarker.headline && (
                      <p style={{ margin: '4px 0' }}>{selectedMarker.headline}</p>
                    )}
                    <p style={{ margin: '4px 0' }}>
                      {selectedMarker.city}, {selectedMarker.province}
                    </p>
                  </div>
                </InfoWindow>
              )}
            </Map>
          </APIProvider>
        </div>
        {isWaitingForResponse && (
          <div className={classes.loading}>
            <LinearProgress style={{ width: '100%' }} />
          </div>
        )}
        {allowAISearch && <p className={classes.ai_copy}>
          The AI search may have unexpected results, you can always use the manual search if you're not finding what you're looking for.
        </p>}
      </Grid>
      <Grid item xs={12}>
        <SearchSidebar
          layout="horizontal"
          searchInputValue={searchInputValue}
          setSearchInputValue={setSearchInputValue}
          searchParams={searchParams}
          handleSearchChange={handleSearchChange}
          handleSearchSubmit={handleSearchSubmit}
          handleKeyPress={handleKeyPress}
          data={data}
          setSearchParams={setSearchParams}
          defaultSearchParams={defaultSearchParams}
          conversationHistory={conversationHistory}
          resetConversation={() => {
            setConversationHistory([
              {type: 'ai',
              message: "Hi! I'm your search assistant tell me exactly what you're looking for!"}
            ]);
            setSearchParams(defaultSearchParams);
            handleSearchSubmit();
          }}
          isLoading={isWaitingForResponse}
          refetch={refetch}
        />
      </Grid>
    </Grid>
  );
};

export default UserMapSearch;